import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BookableService {
  id: string;
  name: string;
  description: string | null;
  duration_minutes: number;
  price_cents: number;
  currency: string;
  color: string;
  deposit_amount_cents: number;
  require_prepayment: boolean;
  payment_timing?: 'upon_booking' | 'at_appointment' | null;
  /** null = all otherwise bookable clinicians; otherwise the service subset. */
  eligible_practitioner_ids?: string[] | null;
}

export interface BookableClinicianScheduleException {
  exception_type: 'unavailable' | 'custom_hours' | 'meeting' | 'vacation' | 'sick';
  starts_on: string;
  ends_on: string;
  starts_time: string | null;
  ends_time: string | null;
}

export interface BookableClinician {
  id: string;
  name: string;
  title: string | null;
  photo_url: string | null;
  bio: string | null;
  working_hours?: Record<string, BookingHours | undefined>;
  schedule_exceptions?: BookableClinicianScheduleException[];
}

export interface BookingHours {
  enabled: boolean;
  start: string;
  end: string;
}

export interface HoursExceptionPublic {
  date: string;
  exception_type: 'closed' | 'custom_hours';
  periods: Array<{ start: string; end: string }>;
}

export interface BookableSettings {
  advance_days: number;
  min_hours_before: number;
  online_booking_hours: Record<string, BookingHours>;
  hours_exceptions?: HoursExceptionPublic[];
  payment_timing?: string;
  payment_methods?: {
    card?: boolean;
    mobilepay?: boolean;
    invoice?: boolean;
    punch_card?: boolean;
  };
  cancellation_deadline_hours?: number;
  allow_patient_rescheduling?: boolean;
  clinic_email?: string | null;
  clinic_phone?: string | null;
  /** Presentation only. Missing/undefined defaults to showing practitioners. */
  show_practitioners_in_online_booking?: boolean;
}

export interface BookablePunchCardTemplate {
  id: string;
  name: string;
  description: string | null;
  total_clips: number;
  price_cents: number;
  currency: string;
  expires_months: number | null;
}

export interface BookableServicesData {
  clinic_id: string;
  clinic_name: string;
  clinic_logo: string | null;
  primary_color: string;
  services: BookableService[];
  clinicians: BookableClinician[];
  punch_card_templates?: BookablePunchCardTemplate[];
  settings: BookableSettings;
}

export interface AvailableSlot {
  start: string;
  end: string;
  clinician_id: string;
}

export interface PatientInput {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  cpr?: string;
  notes?: string;
}

export interface BookAppointmentInput {
  clinic_slug: string;
  service_id: string;
  clinician_id: string;
  start_time: string;
  patient: PatientInput;
  punch_card_id?: string;
}

export interface BookAppointmentResult {
  appointment_id: string;
  confirmation_code: string;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Fetches bookable services, clinicians, and settings for a clinic's public
 * booking widget. Uses the public-list-bookable-services Edge Function.
 */
export function useBookableServices(clinicSlug: string | null | undefined) {
  return useQuery({
    queryKey: ['public-booking', 'services', clinicSlug ?? null] as const,
    enabled: Boolean(clinicSlug),
    queryFn: async (): Promise<BookableServicesData> => {
      if (!clinicSlug) throw new Error('clinic_slug is required');
      const { data, error } = await supabase.functions.invoke<{
        ok: true;
        data: BookableServicesData;
      }>('public-list-bookable-services', {
        method: 'POST',
        body: { clinic_slug: clinicSlug },
      });
      if (error) throw error;
      if (!data?.ok)
        throw new Error(
          (data as unknown as { error: { message: string } })?.error?.message ?? 'Unknown error',
        );
      return data.data;
    },
    staleTime: 30_000,
    refetchOnMount: 'always',
    retry: false,
  });
}

export async function fetchAvailableSlots(input: {
  clinicSlug: string;
  serviceId: string;
  clinicianId?: string | null;
  date: string;
}): Promise<AvailableSlot[]> {
  const { data, error } = await supabase.functions.invoke<{
    ok: true;
    data: { slots: AvailableSlot[] };
  }>('public-get-available-slots', {
    method: 'POST',
    body: {
      clinic_slug: input.clinicSlug,
      service_id: input.serviceId,
      clinician_id: input.clinicianId ?? null,
      date: input.date,
    },
  });
  if (error) throw error;
  if (!data?.ok)
    throw new Error(
      (data as unknown as { error: { message: string } })?.error?.message ?? 'Unknown error',
    );
  return data.data.slots;
}

/**
 * Fetches available time slots for a given service, clinician (optional), and date.
 * Uses the public-get-available-slots Edge Function.
 */
export function useAvailableSlots(
  clinicSlug: string | null | undefined,
  serviceId: string | null | undefined,
  clinicianId: string | null | undefined,
  date: string | null | undefined,
) {
  return useQuery({
    queryKey: [
      'public-booking',
      'slots',
      clinicSlug ?? null,
      serviceId ?? null,
      clinicianId ?? null,
      date ?? null,
    ] as const,
    enabled: Boolean(clinicSlug && serviceId && date),
    queryFn: async (): Promise<AvailableSlot[]> => {
      if (!clinicSlug || !serviceId || !date) return [];
      return fetchAvailableSlots({
        clinicSlug,
        serviceId,
        clinicianId,
        date,
      });
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * Mutation for creating an appointment via the public booking widget.
 * Calls the book-as-patient Edge Function.
 */
export function useBookAppointment() {
  return useMutation({
    mutationFn: async (input: BookAppointmentInput): Promise<BookAppointmentResult> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: true;
        data: BookAppointmentResult;
      }>('book-as-patient', {
        method: 'POST',
        body: input,
      });

      // Handle network/transport errors
      if (error) {
        const err = new Error(error.message ?? 'Network error') as Error & { code?: string };
        // Supabase Functions errors have a context property with status
        const status = (error as { context?: { status?: number } }).context?.status;
        if (status === 409) {
          err.code = 'conflict';
        } else if (status === 429) {
          err.code = 'rate-limited';
        } else if (status === 400) {
          err.code = 'invalid-input';
        } else if (status === 404) {
          err.code = 'not-found';
        } else if (status === 401 || status === 403) {
          err.code = 'unauthorized';
        } else if (status && status >= 500) {
          err.code = 'internal-error';
        } else {
          err.code = 'internal-error';
        }
        throw err;
      }

      // Handle API response errors
      if (!data?.ok) {
        const apiError = (data as unknown as { error: { code: string; message: string } })?.error;
        const err = new Error(apiError?.message ?? 'Booking failed') as Error & { code?: string };
        err.code = apiError?.code;
        throw err;
      }

      return data.data;
    },
  });
}
